// ── CCC-866 (ARC-CO / PLC election and contract) — the Base Acres tab's signed-election section ──
//
// Sits inside FsaBaseAcresPanel, beside the 156EZ upload and with the same UX: pick the farm,
// upload, see the read and its reconciliation BEFORE anything is saved, save, view the original.
// Every figure comes from src/ccc866.js; the PDF reading (text + drawn boxes + form fields) from
// src/ccc866Pdf.js, handed in by App.jsx as api.readCccPages so this file never loads pdf.js.
//
// ⚠ THE SIGNED ELECTION OUTRANKS THE 156EZ's "None" / "Default". The parent panel applies
// electionOverrides(m866) to its own model, so the program cells read "elected on CCC-866".
// ⚠ A SHARE IS NOT 100%. The customer's own share is matched by NAME (App.jsx normFarmerName);
// no match is said, not guessed. Payment base acres = base acres x the customer's share.
import React, { useEffect, useMemo, useState } from "react";
import * as C from "./ccc866";

const titleCase = (s) => String(s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const ac = (v) => (v == null ? "—" : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const pct = (v) => (v == null ? "—" : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`);
const CROP_LABEL = { CORN: "Corn", SOYBEANS: "Soybeans", WHEAT: "Wheat", OATS: "Oats" };
const cropLabel = (c) => CROP_LABEL[c] || titleCase(c);
const dateLabel = (iso) => {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : null;
};
const missingSchema = (e) => /42P01|PGRST205|PGRST202|does not exist|schema cache|Could not find the (table|function)/i.test(String((e && e.message) || e));

export function Ccc866Section({ farmer, farmerId, farmerName, model156, api, ui, isMobile = false, onModel }) {
  const { T, uiCard, uiLabel, uiNum, UiSpinner } = ui;
  const [ups, setUps] = useState([]);              // ccc866_uploads rows: ONE PER PROGRAM YEAR PER ADMIN COUNTY (sql/116)
  const yearList = useMemo(() => [...new Set(ups.map((u) => u.program_year))], [ups]);
  const [year, setYear] = useState(null);
  const [stored, setStored] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [schemaMissing, setSchemaMissing] = useState(false);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reload, setReload] = useState(0);

  const keys = useMemo(() => (farmer && api.nameKeys ? api.nameKeys(farmer) : []), [farmer]);
  const norm = api.normName || ((s) => String(s || "").toUpperCase());

  useEffect(() => {
    let alive = true;
    setStored(null); setErr(null); setUps([]);
    if (!farmerId) return undefined;
    setLoading(true);
    (async () => {
      try {
        const u = await api.fetchRows("ccc866_uploads", { filters: [`farmer_id=eq.${farmerId}`], order: "program_year.desc", fresh: true });
        if (!alive) return;
        setSchemaMissing(false); setUps(u || []);
        const y = (u || []).some((x) => x.program_year === year) ? year : ((u || [])[0] ? u[0].program_year : null);
        const yearRows = (u || []).filter((x) => x.program_year === y);
        setYear(y);
        if (y == null) { setLoading(false); return; }
        const f = [`farmer_id=eq.${farmerId}`, `program_year=eq.${y}`];
        const [elections, shares] = await Promise.all([
          api.fetchRows("ccc866_farm_elections", { filters: f, fresh: true }),
          api.fetchRows("ccc866_producer_shares", { filters: f, fresh: true }),
        ]);
        if (!alive) return;
        setStored({ upload: C.mergeUploads866(yearRows), uploads: yearRows, elections: elections || [], shares: shares || [] });
      } catch (e) {
        if (!alive) return;
        if (missingSchema(e)) setSchemaMissing(true); else setErr(String((e && e.message) || e));
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [farmerId, year, reload]);

  const model = useMemo(() => (stored ? C.electionModel(stored, { keys, norm }) : null), [stored, keys]);
  const preview = useMemo(() => (pending ? C.electionModel(C.storedFromPayload866(pending.payload), { keys, norm }) : null), [pending, keys]);
  const shown = preview || model;
  // the parent applies the signed election to its 156EZ model; a preview is not signed-and-saved, so it never does
  useEffect(() => { if (onModel) onModel(model); }, [model]);
  const disagreements = useMemo(() => (model && model156 && model156.crop_year === model.program_year ? C.compareWith156ez(model, model156) : []), [model, model156]);

  const onFile = async (file) => {
    if (!file) return;
    setErr(null); setPending(null); setBusy("Reading the CCC-866…");
    try {
      const pages = await api.readCccPages(file);
      if (!pages || !pages.length) throw new Error("This PDF has no readable pages.");
      if (!pages.some((p) => (p.lines || []).length)) throw new Error("This PDF has no text layer to read (a scan?) — it cannot be read automatically.");
      const doc = C.parse866(pages);
      if (!doc.farms.length) throw new Error("No CCC-866 form was found in this file. Is it a CCC-866?");
      const noAdmin = C.missingAdmin866(doc);
      if (noAdmin.length) throw new Error(`Not saved: the administrative state/county code could not be read for FSA farm${noAdmin.length === 1 ? "" : "s"} ${noAdmin.join(", ")}. Each CCC-866 is filed under its FSA administrative county, so one whose county cannot be read cannot be stored.`);
      const rc = C.reconcile866(doc);
      setPending({ file, doc, rc, payload: { ...C.rowsFor866(doc, { fileName: file.name }), farmer_id: farmerId } });
    } catch (e) { setErr(String((e && e.message) || e)); }
    setBusy(null);
  };

  const save = async () => {
    if (!pending || !farmerId) return;
    setErr(null); setBusy("Saving…");
    try {
      const orig = await api.storeOriginal({ file: pending.file, cropYear: pending.doc.program_year, docType: "ccc_866", farmerId });
      if (orig && orig.status === "failed") throw new Error("The PDF could not be stored: " + orig.error);
      await api.rpc("ccc866_replace", { p: { ...pending.payload, document_id: (orig && orig.id) || null, storage_path: (orig && orig.path) || null } });
      setPending(null); setYear(pending.doc.program_year); setReload((n) => n + 1);
    } catch (e) {
      if (missingSchema(e)) setErr("Saving needs the database function from sql/115 (ccc866_replace), which is not applied yet. The read above is correct; nothing was saved.");
      else setErr(String((e && e.message) || e));
    }
    setBusy(null);
  };

  const viewPdf = async (p) => {
    if (!p) return;
    try { window.open(await api.signedUrl(p), "_blank"); } catch (e) { setErr(String((e && e.message) || e)); }
  };

  const card = { ...uiCard(isMobile ? 12 : 16), marginBottom: 14 };
  const btn = (primary, disabled) => ({ fontSize: 12.5, fontWeight: 800, padding: "7px 14px", borderRadius: 8, cursor: disabled ? "default" : "pointer",
    border: primary ? "none" : `1px solid ${T.lineStrong}`, background: disabled ? "#b7c7b7" : primary ? T.green : "#fff", color: primary ? "#fff" : T.inkSoft });

  if (!farmerId) return null;

  const review = pending && (() => {
    const { doc, rc } = pending;
    // Saving replaces ONLY the administrative counties this document contains; other counties on file stay.
    const pairs = C.adminPairs866(doc);
    const onFile = ups.filter((u) => u.program_year === doc.program_year);
    const hit = (u) => pairs.some((p) => p.state_code === u.state_code && p.county_code === u.county_code);
    const replaced = onFile.filter(hit), kept = onFile.filter((u) => !hit(u));
    const nameOf = (u) => { const m = pairs.find((p) => p.county_code === u.county_code && p.state_code === u.state_code); return m ? titleCase(m.county_name) : `county ${u.county_code}`; };
    return (
      <div style={{ ...card, border: `1px solid ${rc.ok ? "#9cc79c" : "#e2c98f"}`, background: rc.ok ? "#f4faf4" : "#fdf8ec" }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: rc.ok ? T.green : "#8a6d1f" }}>
          {rc.ok ? "CCC-866 read and reconciled — not saved yet" : "CCC-866 read, but it does not reconcile — check before saving"}
        </div>
        <div style={{ fontSize: 12.5, color: T.body, marginTop: 4, lineHeight: 1.5 }}>
          {pending.file.name} · program year <b>{doc.program_year || "unknown"}</b> · {doc.farms.length} FSA farm form{doc.farms.length === 1 ? "" : "s"}
          {" "}· {doc.skipped.boilerplate} boilerplate and {doc.skipped.blank} blank page(s) skipped{doc.unparsed.length ? ` · ${doc.unparsed.length} line(s) not read` : " · every line read"}
        </div>
        <div style={{ fontSize: 12.5, marginTop: 6, color: T.body }}>Administrative {pairs.length === 1 ? "county" : "counties"} in this file: <b>{pairs.map((p) => titleCase(p.county_name)).join(", ")}</b>.</div>
        {replaced.length > 0 && <div style={{ fontSize: 12.5, marginTop: 6 }}>Saving <b>replaces</b> the {doc.program_year} CCC-866 for {replaced.map(nameOf).join(", ")}.</div>}
        {kept.length > 0 && <div style={{ fontSize: 12.5, marginTop: 4, color: T.muted }}>The {doc.program_year} CCC-866 on file for {kept.map((u) => `county ${u.county_code}`).join(", ")} stays as it is.</div>}
        {rc.failures.length > 0 && <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 12, color: "#6b4f12" }}>{rc.failures.slice(0, 12).map((c, i) => <li key={i}>{c.detail}</li>)}</ul>}
        {rc.notes.length > 0 && <div style={{ fontSize: 11.5, color: T.muted, marginTop: 6 }}>For information: {rc.notes.slice(0, 6).join("; ")}{rc.notes.length > 6 ? `; … and ${rc.notes.length - 6} more` : ""}.</div>}
        {doc.unparsed.length > 0 && <details style={{ marginTop: 6, fontSize: 12 }}><summary>Lines not read ({doc.unparsed.length})</summary>
          <ul style={{ margin: "4px 0 0", paddingLeft: 18, color: T.muted }}>{doc.unparsed.slice(0, 40).map((u, i) => <li key={i}>page {u.page} · {u.where}: <code>{u.line}</code></li>)}</ul></details>}
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <button onClick={save} disabled={!!busy || doc.program_year == null || schemaMissing} style={btn(true, !!busy || doc.program_year == null || schemaMissing)}>
            {rc.ok ? `Save CCC-866 to ${farmerName}` : "Save anyway, with the warning"}
          </button>
          <button onClick={() => setPending(null)} style={btn(false)}>Discard</button>
        </div>
      </div>
    );
  })();

  // ── BASE ACRE SUMMARY — every farm on file (or in the preview) added up by crop and program ──
  const summary = shown ? C.baseAcreSummary866(shown) : null;   // cheap, and below an early return — so not a hook
  const sumTable = (rows, total) => {
    const anyUnknown = rows.some((r) => r.unknown_share_acres > 0);
    const heads = ["Crop", "Elected", "Farms", "Base acres", `${farmerName ? farmerName + "’s" : "Customer’s"} payment base acres`, ...(anyUnknown ? ["Base acres where the customer is not named"] : [])];
    const td = (right, bold) => ({ ...(right ? uiNum : {}), padding: "5px 6px", textAlign: right ? "right" : "left", fontWeight: bold ? 800 : 400 });
    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 560 }}>
          <thead><tr>{heads.map((h, i) => <th key={h} style={{ ...uiLabel, textAlign: i < 2 ? "left" : "right", padding: "5px 6px", borderBottom: `1px solid ${T.line}` }}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.crop + "|" + r.program} style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
                <td style={td(false, true)}>{cropLabel(r.crop)}</td>
                <td style={td(false)}>{r.program || "not read"}</td>
                <td style={td(true)}>{r.farms}</td>
                <td style={td(true)}>{ac(r.base_acres)}{r.base_unread ? <span style={{ color: "#8a6d1f" }}> + {r.base_unread} not read</span> : null}</td>
                <td style={td(true, true)}>{ac(r.pay_acres)}</td>
                {anyUnknown && <td style={td(true)}>{r.unknown_share_acres > 0 ? ac(r.unknown_share_acres) : "—"}</td>}
              </tr>
            ))}
            {total && rows.length > 1 && (
              <tr style={{ background: "#f2f6f2" }}>
                <td style={td(false, true)} colSpan={3}>All crops</td>
                <td style={td(true, true)}>{ac(total.base_acres)}</td>
                <td style={td(true, true)}>{ac(total.pay_acres)}</td>
                {anyUnknown && <td style={td(true, true)}>{total.unknown_share_acres > 0 ? ac(total.unknown_share_acres) : "—"}</td>}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };
  const baseSummary = summary && summary.rows.length > 0 && (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.ink }}>Base acre summary · {shown.program_year}</div>
        <div style={{ fontSize: 12, color: T.muted }}>{preview ? "from the document just read — not saved yet" : `${shown.farms.length} FSA farm${shown.farms.length === 1 ? "" : "s"} on file`}</div>
      </div>
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>
        Base acres by crop and elected program. Payment base acres are each farm’s base acres × {farmerName ? `${farmerName}’s` : "the customer’s"} share on that form; a farm where the customer is not named adds nothing to them and is shown separately.
      </div>
      {sumTable(summary.rows, summary.total)}
      {summary.counties.length > 1 && summary.counties.map((c) => (
        <div key={c.state_code + "|" + c.county_code} style={{ marginTop: 10 }}>
          <div style={{ ...uiLabel, margin: "4px 0" }}>{titleCase(c.county_name)} County{c.state_name ? `, ${titleCase(c.state_name)}` : ""}</div>
          {sumTable(c.rows, null)}
        </div>
      ))}
    </div>
  );

  const farms = shown && shown.farms.length > 0 && (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.ink }}>ARC/PLC election · CCC-866 · {shown.program_year}</div>
        <div style={{ fontSize: 12, color: T.muted }}>{preview ? "preview of the document just read" : `signed election on file${shown.file_name ? ` · ${shown.file_name}` : ""}`}</div>
      </div>
      <div style={{ fontSize: 12, color: T.muted, marginBottom: 10 }}>
        This is the producers’ signed election and it outranks the “None” / “Default” text on the 156EZ. Payment base acres are base acres × {farmerName ? `${farmerName}’s` : "the customer’s"} share, where the customer is on the form.
      </div>
      {!preview && (shown.counties || []).length > 0 && <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>On file for {shown.program_year}: {shown.counties.map((c) => `county ${c.county_code}${c.form_count != null ? ` (${c.form_count} form${c.form_count === 1 ? "" : "s"})` : ""}`).join(" · ")}. All counties are combined below and grouped by county.</div>}
      {!preview && shown.identity_ok === false && <div style={{ fontSize: 12, color: "#6b4f12", background: "#fdf8ec", border: "1px solid #e6c98a", borderRadius: 6, padding: "6px 9px", marginBottom: 10 }}>
        Saved with {shown.identity_failures.length} reconciliation warning(s): {shown.identity_failures.slice(0, 3).join("; ")}</div>}
      {disagreements.length > 0 && <div style={{ fontSize: 12, color: "#8a1f1f", background: "#fbeeee", border: "1px solid #e2b4b4", borderRadius: 6, padding: "6px 9px", marginBottom: 10 }}>
        <b>CCC-866 and the stored 156EZ disagree</b> ({disagreements.length}):
        <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{disagreements.slice(0, 12).map((d, i) => <li key={i}>{d.detail}</li>)}{disagreements.length > 12 && <li>… and {disagreements.length - 12} more</li>}</ul></div>}
      {shown.farms.map((f, i) => {
        const crops = Object.keys(f.crops).sort();
        const newCounty = i === 0 || shown.farms[i - 1].county_code !== f.county_code || shown.farms[i - 1].state_code !== f.state_code;
        const manyCounties = new Set(shown.farms.map((x) => x.state_code + "|" + x.county_code)).size > 1;
        return (
          <React.Fragment key={`${f.state_code}|${f.county_code}|${f.farm_number}`}>
          {manyCounties && newCounty && <div style={{ ...uiLabel, margin: "10px 0 6px" }}>{titleCase(f.county_name)} County{f.state_name ? `, ${titleCase(f.state_name)}` : ""}</div>}
          <div style={{ border: `1px solid ${T.lineSoft}`, borderRadius: 10, marginBottom: 8, padding: "9px 12px", background: "#fff" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
              <div style={{ fontWeight: 800, color: T.ink }}>Farm {f.farm_number}</div>
              <div style={{ fontSize: 11.5, color: T.muted }}>{titleCase(f.county_name)}{f.state_name ? `, ${titleCase(f.state_name)}` : ""}</div>
              <div style={{ fontSize: 11.5, color: T.muted }}>
                {f.signed_date ? `elected on CCC-866, signed ${dateLabel(f.signed_date)}` : "CCC-866 on file — no signature date read"}
                {f.multi_year_through ? ` · multi-year contract through ${f.multi_year_through}` : ""}
              </div>
            </div>
            {!f.name_matched && <div style={{ fontSize: 12, color: "#8a6d1f", marginTop: 4 }}>
              {f.name_ambiguous ? `More than one producer on this form matches ${farmerName}’s name — no share is assumed.`
                : `${farmerName || "The customer"} is not named as a producer on this form (${f.producers.map((p) => p.name).filter(Boolean).join(", ") || "no producer read"}) — no share is assumed.`}</div>}
            <div style={{ overflowX: "auto", marginTop: 6 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 560 }}>
                <thead><tr>{["Crop", "Elected", "Base acres", "PLC yield", f.name_matched ? "Customer’s share" : "Share", f.name_matched ? "Payment base acres" : "Other producers"].map((h, i) =>
                  <th key={h} style={{ ...uiLabel, textAlign: i < 2 ? "left" : "right", padding: "5px 6px", borderBottom: `1px solid ${T.line}` }}>{h}</th>)}</tr></thead>
                <tbody>{crops.map((cr) => {
                  const c = f.crops[cr];
                  return (
                    <tr key={cr} style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
                      <td style={{ padding: "5px 6px", fontWeight: 700 }}>{cropLabel(cr)}</td>
                      <td style={{ padding: "5px 6px" }}>{c.program || "—"}</td>
                      <td style={{ ...uiNum, padding: "5px 6px", textAlign: "right" }}>{ac(c.base_acres)}</td>
                      <td style={{ ...uiNum, padding: "5px 6px", textAlign: "right" }}>{c.plc_yield == null ? "—" : c.plc_yield}</td>
                      <td style={{ ...uiNum, padding: "5px 6px", textAlign: "right", fontWeight: f.name_matched ? 800 : 400 }}>{f.name_matched ? pct(c.my_share) : c.listed_share ? pct(c.listed_share) + " listed" : "—"}</td>
                      <td style={{ ...uiNum, padding: "5px 6px", textAlign: "right", fontWeight: f.name_matched ? 800 : 400 }}>
                        {f.name_matched ? ac(c.my_base_acres)
                          : c.over_100 ? <span style={{ color: "#8a1f1f" }}>listed shares exceed 100%</span>
                          : c.other_share ? `${pct(c.other_share)} not on this form` : "—"}
                      </td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
            {f.name_matched && crops.some((cr) => f.crops[cr].other_share > 0 || f.crops[cr].over_100) && <div style={{ fontSize: 11.5, color: T.muted, marginTop: 4 }}>
              {crops.filter((cr) => f.crops[cr].over_100).length
                ? <span style={{ color: "#8a1f1f" }}>Listed shares for {crops.filter((cr) => f.crops[cr].over_100).map(cropLabel).join(", ")} exceed 100% — check the form.</span>
                : "Where the listed shares total less than 100%, the rest is held by producers not on this form."}</div>}
            {f.producers.length > 1 && <div style={{ fontSize: 11.5, color: T.muted, marginTop: 4 }}>
              Producers on this form: {f.producers.map((p) => `${p.name}${p.refused_arc || p.refused_plc ? " (payments refused)" : ""}`).join(" · ")}</div>}
          </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <div>
      <div style={card}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
          <div style={{ minWidth: 0, flex: "1 1 260px" }}>
            <div style={uiLabel}>CCC-866 · ARC-CO / PLC election and contract</div>
            <div style={{ fontSize: 12.5, color: T.muted, marginTop: 3 }}>The signed election and each producer’s payment share. One PDF can hold many FSA farms.</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {yearList.length > 1 && (
              <select value={year || ""} onChange={(e) => setYear(Number(e.target.value))} aria-label="Program year"
                style={{ fontSize: 12.5, padding: "6px 8px", borderRadius: 7, border: `1px solid ${T.lineStrong}` }}>
                {yearList.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            <label style={{ ...btn(true, !!busy), display: "inline-block" }}>
              {stored ? "Upload a newer CCC-866" : "Upload CCC-866"}
              <input type="file" accept="application/pdf,.pdf" style={{ display: "none" }} disabled={!!busy}
                onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; onFile(f); }} />
            </label>
            {stored && stored.upload && !pending && (stored.upload.storage_paths || []).map((p, i, a) => <button key={p} onClick={() => viewPdf(p)} style={btn(false)}>{a.length > 1 ? `View CCC-866 ${i + 1} of ${a.length}` : "View CCC-866"}</button>)}
          </div>
        </div>
        {busy && <div style={{ marginTop: 10, fontSize: 12.5, color: T.muted }}><UiSpinner />{busy}</div>}
        {err && <div style={{ marginTop: 10, fontSize: 12.5, color: T.danger }}>{err}</div>}
        {schemaMissing && <div style={{ marginTop: 10, fontSize: 12.5, color: T.warn }}>The CCC-866 tables (sql/115) are not in the database yet. A CCC-866 can still be read and checked here; it cannot be saved.</div>}
        {loading && <div style={{ marginTop: 10, color: T.muted, fontSize: 12.5 }}><UiSpinner />Loading…</div>}
        {!loading && !shown && !pending && !schemaMissing && !err && <div style={{ marginTop: 10, color: T.muted, fontSize: 12.5 }}>No CCC-866 on file for {farmerName}.</div>}
      </div>
      {review}
      {baseSummary}
      {farms}
    </div>
  );
}
